import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import PickerPopover from './PickerPopover'
import { makeT } from '../../../group_leader/helpers/i18n'
import { useListKeyboardNav } from '../../hooks/useListKeyboardNav'

const t = makeT('group_leader.event_setup.players.picker')

// Picking one of the group's past events, from a list too long to scroll.
const EventPicker = ({ events, selectedId, onSelect }) => {
  const [ open, setOpen ] = useState(false)
  // null while the field shows the chosen event, a string once the leader types.
  const [ query, setQuery ] = useState(null)
  const [ rect, setRect ] = useState(null)
  const fieldRef = useRef(null)
  const listRef = useRef(null)

  const selected = events.find((event) => String(event.id) === String(selectedId))

  const matches = useMemo(() => {
    const needle = (query || '').trim().toLowerCase()
    if (!needle) { return events }

    return events.filter((event) => event.name.toLowerCase().includes(needle))
  }, [ events, query ])

  const openList = () => {
    setRect(fieldRef.current.getBoundingClientRect())
    setOpen(true)
  }

  const close = () => {
    setOpen(false)
    setQuery(null)
    reset()
  }

  const choose = (event) => {
    onSelect(event.id)
    close()
  }

  const { active, reset, onKeyDown } = useListKeyboardNav(matches, { onPick: choose, onEscape: close })

  useEffect(() => {
    listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' })
  }, [ active ])

  const handleChange = (event) => {
    setQuery(event.target.value)
    reset()
    if (!open) { openList() }
  }

  return (
    <>
      <div className="gl-event-setup-event-picker" ref={fieldRef}>
        <input
          type="text"
          id="gl-event-search"
          className="gl-event-setup-event-picker-input"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          aria-controls="gl-event-listbox"
          aria-activedescendant={active >= 0 ? `gl-event-opt-${active}` : undefined}
          aria-label={t('event_search_aria')}
          placeholder={t('event_placeholder')}
          value={query === null ? (selected?.name || '') : query}
          onChange={handleChange}
          onMouseDown={() => { if (!open) { openList() } }}
          onKeyDown={onKeyDown}
        />
        <span className="gl-event-setup-event-picker-caret" aria-hidden="true">⌄</span>
      </div>

      {open && rect && (
        <PickerPopover rect={rect} onClose={close} mobileSheet matchWidth manageFocus={false}>
          <div id="gl-event-listbox" ref={listRef} role="listbox" aria-label={t('event_label')}>
            {matches.map((event, index) => (
              <button
                key={event.id}
                id={`gl-event-opt-${index}`}
                data-index={index}
                type="button"
                role="option"
                aria-selected={String(event.id) === String(selectedId)}
                className={`gl-event-setup-slot-picker-item${index === active ? ' is-active' : ''}`}
                onClick={() => choose(event)}
              >
                {event.name}
              </button>
            ))}
            {matches.length === 0 && (
              <p className="gl-event-setup-event-picker-empty">{t('event_no_matches')}</p>
            )}
          </div>
        </PickerPopover>
      )}
    </>
  )
}

EventPicker.propTypes = {
  events: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  })).isRequired,
  selectedId: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
}

export default EventPicker
